import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader, Mail } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://touchdownleague.com/api';

function VerifyEmailPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
    const [message, setMessage] = useState('');

    useEffect(() => {
        const verifyEmail = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/auth/verify-email/${token}`);
                setStatus('success');
                setMessage(response.data.message);

                // Rediriger vers la page de connexion après 3 secondes
                setTimeout(() => {
                    navigate('/?view=login');
                }, 3000);
            } catch (error) {
                setStatus('error');
                setMessage(error.response?.data?.error || 'Une erreur est survenue lors de la vérification');
            }
        };

        if (token) {
            verifyEmail();
        }
    }, [token, navigate]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                <div className="bg-white rounded-lg shadow-2xl p-8">
                    {/* Header */}
                    <div className="text-center mb-6">
                        <div className="inline-block p-3 bg-blue-100 rounded-full mb-4">
                            <Mail className="w-8 h-8 text-blue-600" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-800">Vérification d'email</h1>
                    </div>

                    {/* Status Content */}
                    <div className="text-center">
                        {status === 'loading' && (
                            <div className="py-8">
                                <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                                <p className="text-gray-600">Vérification en cours...</p>
                            </div>
                        )}

                        {status === 'success' && (
                            <div className="py-8">
                                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                                <h2 className="text-xl font-semibold text-green-600 mb-2">Email vérifié !</h2>
                                <p className="text-gray-600 mb-4">{message}</p>
                                <p className="text-sm text-gray-500">
                                    Redirection vers la page de connexion dans 3 secondes...
                                </p>
                                <button
                                    onClick={() => navigate('/?view=login')}
                                    className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Se connecter maintenant
                                </button>
                            </div>
                        )}

                        {status === 'error' && (
                            <div className="py-8">
                                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                                <h2 className="text-xl font-semibold text-red-600 mb-2">Erreur de vérification</h2>
                                <p className="text-gray-600 mb-6">{message}</p>
                                <div className="space-y-3">
                                    <button
                                        onClick={() => navigate('/?view=login')}
                                        className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                        Retour à la connexion
                                    </button>
                                    <button
                                        onClick={() => navigate('/?view=register')}
                                        className="w-full px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                    >
                                        Créer un nouveau compte
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="mt-6 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
                        <p>© 2024 Touchdown League</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VerifyEmailPage;
